import { useMemo } from 'react';
import Highcharts, { TooltipFormatterContextObject } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import {
  findRollupByGroupingValue,
  groupingValueFor,
  totalDimsFor,
} from 'components/insights/careTransitions/shared/slideout/rollupHelpers';
import { Metrics } from 'components/insights/constants';
import { formatDaysValue, formatPercentage, formatWholeNumber } from 'components/insights/formatting';
import { colors } from 'styles/theme/colors';
import textStyles from 'styles/theme/textStyles';

import MetricDetailLayout from '../../../shared/slideout/MetricDetailLayout';
import { readGrandTotal, readGrandTotalOrZero } from '../../../shared/slideout/rollupHelpers';
import { StatTile } from '../../../shared/slideout/statTiles';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from '../constants';
import { type MetricHeaderProps } from '../NetworkPerformanceSlideout';

import { type ScrollConfig } from './chartHelpers';

// Register custom column2 series type for side-by-side stacking (same pattern as old Insights).
// Highcharts silently overwrites if already registered, so this is safe even if BreakoutChart loads too.
Highcharts.seriesType('column2', 'column', {});

const POINT_WIDTH = 40;

export default function ReadmissionRateHeader({
  loading,
  rollups,
  highestEntity,
  entityRows,
  entityLabel,
}: MetricHeaderProps) {
  const readmissionRate = readGrandTotal(rollups, Metrics.READMISSION_RATE);
  const readmissionsSum = readGrandTotalOrZero(rollups, Metrics.READMISSIONS_SUM);
  const totalDischarges = readGrandTotalOrZero(rollups, Metrics.ID_COUNT);
  const avgLos = readGrandTotal(rollups, Metrics.READMISSION_LENGTH_OF_STAY_AVERAGE);

  const { chartOptions, scrollConfig } = useMemo(() => {
    // Get per-entity data from rollups (same approach as extractEntityRows in the hook)
    const totalDims = totalDimsFor(rollups);
    const entityTarget = groupingValueFor(totalDims, new Set([0]));
    const entityRollup = findRollupByGroupingValue(rollups, entityTarget);

    const names = entityRollup?.dimensions.getCurrentDimensionValuesByIndex(0) ?? [];
    const rates = entityRollup?.metrics.getCurrentSeries(Metrics.READMISSION_RATE) ?? [];
    const discharges = entityRollup?.metrics.getCurrentSeries(Metrics.ID_COUNT) ?? [];
    const readmissions = entityRollup?.metrics.getCurrentSeries(Metrics.READMISSIONS_SUM) ?? [];

    // Sort by readmission rate descending (match the entityRows sort order)
    const indices = names.map((_, i) => i).sort((a, b) => (rates[b] ?? 0) - (rates[a] ?? 0));
    const sortedNames = indices.map((i) => names[i]);
    const sortedDischarges = indices.map((i) => discharges[i] ?? 0);
    const sortedReadmissions = indices.map((i) => readmissions[i] ?? 0);
    const sortedRates = indices.map((i) => rates[i]);

    const scrollableMinWidth = (POINT_WIDTH + POINT_WIDTH * 1.5) * sortedNames.length;

    const options: Highcharts.Options = {
      chart: {
        type: 'column',
        style: { fontFamily: textStyles.base.fontFamily },
        height: 280,
      },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        symbolRadius: 6,
        itemStyle: { fontSize: '12px', fontWeight: 'normal', color: colors.black75 },
      },
      xAxis: {
        categories: sortedNames,
        lineColor: colors.black10,
        tickLength: 0,
        labels: { style: { fontSize: '11px', color: colors.black75 } },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: colors.black10,
        labels: { style: { fontSize: '11px', color: colors.black50 } },
      },
      tooltip: { enabled: false },
      plotOptions: {
        column: {
          borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
          pointWidth: POINT_WIDTH,
        },
        // @ts-expect-error column2 does not exist on the plotOptions type, but is custom to this chart
        column2: {
          borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
          pointWidth: POINT_WIDTH,
        },
        series: { animation: false, inactiveOtherPoints: true, events: { legendItemClick: () => false } },
      },
      series: [
        {
          type: 'column',
          name: 'Discharges',
          color: colors.black10,
          data: sortedDischarges.map((val, i) => ({
            y: val,
            label: formatPercentage(sortedRates[i] ?? null),
            dataLabels: {
              enabled: true,
              formatter: function (this: TooltipFormatterContextObject): string {
                return `<span style="font-size:10px;font-weight:normal;">${this.point.label}</span>`;
              },
            },
          })),
        },
        {
          type: 'column2' as 'column',
          name: 'Readmissions',
          color: colors.red100,
          data: sortedReadmissions,
        },
      ],
    };

    const scroll: ScrollConfig = { minWidth: scrollableMinWidth };

    return { chartOptions: options, scrollConfig: scroll };
  }, [rollups]);

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label={METRIC_CONFIGS[NetworkPerformanceMetrics.READMISSION_RATE].primaryMetricLabel}
            loading={loading}
            primary={formatPercentage(readmissionRate)}
            addendum={`${readmissionsSum} of ${totalDischarges} discharges`}
            tone='red'
          />
          <StatTile
            label='Most Readmissions'
            loading={loading}
            primary={highestEntity?.name ?? '\u2014'}
            size='medium'
            addendum={highestEntity ? `${formatPercentage(highestEntity.value)} readmission rate` : undefined}
          />
          <StatTile
            label='Avg. Length of Service for Readmissions'
            loading={loading}
            primary={formatDaysValue(avgLos)}
            addendum={`Based on ${formatWholeNumber(readmissionsSum)} readmissions`}
          />
        </>
      }
      chartTitle={`${METRIC_CONFIGS[NetworkPerformanceMetrics.READMISSION_RATE].label} by ${entityLabel}`}
      chartLoading={loading}
      isEmpty={entityRows.length === 0}
      scrollConfig={scrollConfig}
      emptyMessage='No readmission data to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
